// src/layouts/authentication/sign-in/index.js
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Card from "@mui/material/Card";
import { Typography } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import BasicLayout from "layouts/authentication/components/BasicLayout";
import bgImage from "assets/images/bg-sign-in-basic.jpeg";

import { useAuth } from "context/AuthContext";
import api from "api/axios";
import { universalLogin, csrfBootstrap } from "services/auth";
import { whoAmI } from "services/session";

const TENANT_RE = /^(?!-)[a-z0-9-]{1,63}(?<!-)$/;
const normalizeCompany = (s) =>
  (s || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "")
    .slice(0, 63);

// Fixe tenant côté LS + axios.defaults (filet de sécurité)
const setActiveTenant = (schema) => {
  try {
    localStorage.setItem("company", schema);
  } catch {}
  api.defaults.headers.common["X-Tenant"] = schema;
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[SignIn] setActiveTenant:", schema);
  }
};

// Fabrique des headers figés pour un schéma donné
const useTenantHeaders = (schema) => useMemo(() => ({ "X-Tenant": schema }), [schema]);

const redirectToQuickBooks = (schema, next = "/dashboard") => {
  const base = (api.defaults.baseURL || "").replace(/\/+$/, "");
  const nextEncoded = encodeURIComponent(next);
  const href = `${base}/quickbooks/connect/?schema=${encodeURIComponent(schema)}&next=${nextEncoded}`;
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn("[SignIn] redirectToQuickBooks:", href);
  }
  window.location.href = href;
};

function SignIn() {
  const [company, setCompany] = useState(localStorage.getItem("company") || "");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Dès que l'utilisateur tape une entreprise valide → on fixe X-Tenant par défaut
  useEffect(() => {
    const c = normalizeCompany(company);
    if (c && TENANT_RE.test(c)) setActiveTenant(c);
  }, [company]);

  const tenantHeaders = useTenantHeaders(normalizeCompany(company));

  // Retour QuickBooks ?success=true&company=...&user_id=...
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const success = queryParams.get("success");
    const qCompany = queryParams.get("company");
    const user_id = queryParams.get("user_id");

    const restoreAfterQB = async () => {
      try {
        const c = normalizeCompany(qCompany);
        if (!TENANT_RE.test(c)) throw new Error("Entreprise invalide.");
        setActiveTenant(c);
        const headers = { "X-Tenant": c };

        try {
          localStorage.setItem("user_id", user_id || "");
        } catch {}

        const me = await whoAmI({ headers });
        if (!me || !me.id) throw new Error("Session non établie.");
        login(me, c);

        const qb = await api.get("/quickbooks/me/", { headers });
        const connected = !!qb?.data?.connected;
        if (!connected) {
          redirectToQuickBooks(c, "/dashboard");
          return;
        }
        navigate("/dashboard", { replace: true });
      } catch (err) {
        setError("Échec de connexion : veuillez vous reconnecter.");
        setInitialLoading(false);
      }
    };

    if (success === "true" && qCompany && user_id) {
      restoreAfterQB();
    } else {
      setInitialLoading(false);
    }
  }, [location.search, login, navigate]);

  const handleUniversalLogin = async () => {
    const c = normalizeCompany(company);
    setCompany(c);

    if (!username.trim() || !password.trim() || !c) {
      setError("Entreprise, identifiant et mot de passe sont requis.");
      return;
    }
    if (!TENANT_RE.test(c)) {
      setError("Nom d'entreprise invalide (minuscules/chiffres/tirets, 1–63, sans tiret en bord).");
      return;
    }

    setLoading(true);
    setError("");
    window.__AUTH_LOGIN_FLOW__ = true;

    try {
      setActiveTenant(c);

      // Assure le CSRF + cookie pour le bon tenant (utile en Safari)
      await csrfBootstrap(c);

      // 1) Login universel (avec header figé)
      const data = await universalLogin(
        { username, password, company: c },
        { headers: { "X-Tenant": c } }
      );
      const schema = data?.schema;
      if (!schema) throw new Error("Réponse inattendue du serveur.");
      if (schema !== c) setActiveTenant(schema);

      const headers = { "X-Tenant": schema };

      // 2) whoAmI (header figé)
      const me = await whoAmI({ headers });
      if (!me || !me.id) throw new Error("Session non établie.");
      login(me, schema);
      try {
        localStorage.setItem("user_id", String(me.id || ""));
      } catch {}

      // 3) QuickBooks ?
      const qb = await api.get("/quickbooks/me/", { headers });
      const connected = !!qb?.data?.connected;
      if (!connected) {
        redirectToQuickBooks(schema, "/dashboard");
        return;
      }

      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.message ||
        "Erreur de connexion.";
      setError(msg);
    } finally {
      setLoading(false);
      window.__AUTH_LOGIN_FLOW__ = false;
    }
  };

  if (initialLoading) {
    return (
      <BasicLayout image={bgImage}>
        <Card>
          <MDBox pt={4} pb={3} px={3}>
            <MDTypography variant="h5" fontWeight="medium">
              Connexion en cours...
            </MDTypography>
            <Typography variant="body2" color="textSecondary">
              Vérification de session...
            </Typography>
          </MDBox>
        </Card>
      </BasicLayout>
    );
  }

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox pt={4} pb={3} px={3}>
          <MDTypography variant="h4" fontWeight="medium">
            Connexion
          </MDTypography>
        </MDBox>

        <MDBox pt={0} pb={3} px={3}>
          <MDBox mb={2}>
            <MDInput
              type="text"
              label="Entreprise (tenant)"
              fullWidth
              required
              value={company}
              onChange={(e) => setCompany(normalizeCompany(e.target.value))}
              placeholder="ex: tenant1"
              error={Boolean(company) && !TENANT_RE.test(company)}
              helperText={
                company && !TENANT_RE.test(company)
                  ? "minuscules/chiffres/tirets, 1–63 car., sans tiret au début/fin"
                  : " "
              }
            />
          </MDBox>
          <MDBox mb={2}>
            <MDInput
              type="text"
              label="Nom d'utilisateur ou email"
              fullWidth
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </MDBox>
          <MDBox mb={2}>
            <MDInput
              type="password"
              label="Mot de passe"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </MDBox>

          {error && (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          <MDBox mt={4} mb={1}>
            <MDButton
              variant="gradient"
              color="info"
              fullWidth
              onClick={handleUniversalLogin}
              disabled={loading}
            >
              {loading ? "Connexion..." : "Se connecter"}
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default SignIn;
